/**
 * The admin API. Same session model as the portal — an HttpOnly cookie and a double-submit
 * antiforgery token — because it is the same server; only the endpoints differ.
 */
function readCookie(name) {
  return document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`))
    ?.slice(name.length + 1)
}

async function request(path, { method = 'GET', body } = {}) {
  const headers = {}

  if (body) headers['Content-Type'] = 'application/json'

  if (method !== 'GET' && method !== 'HEAD') {
    const token = readCookie('XSRF-TOKEN')
    if (token) headers['X-XSRF-TOKEN'] = decodeURIComponent(token)
  }

  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: Object.keys(headers).length > 0 ? headers : undefined,
    body: body ? JSON.stringify(body) : undefined,
  })

  if (response.status === 401) {
    const error = new Error('unauthenticated')
    error.unauthenticated = true
    throw error
  }

  if (!response.ok) {
    let detail = `HTTP ${response.status}`
    try {
      const problem = await response.json()
      detail = problem.title ?? problem.detail ?? detail
    } catch {
      // Not a problem document; the status is all we have.
    }
    throw new Error(detail)
  }

  if (response.status === 204 || response.status === 202) return null
  return response.json()
}

export const api = {
  /** The token pair arrives on any safe GET; this one exists to be that GET before login. */
  prime: () => fetch('/api/system/config', { credentials: 'same-origin' }),

  login: (email, password) => request('/api/admin/auth/login', { method: 'POST', body: { email, password } }),
  me: () => request('/api/admin/me'),

  // The ordinary sign-out: the session is the same cookie the portal issues, so the same endpoint
  // ends it. A second one would be a second thing to keep correct.
  logout: () => request('/api/auth/logout', { method: 'POST' }),

  tenants: (query) => request(`/api/admin/tenants${query ? `?query=${encodeURIComponent(query)}` : ''}`),
  tenant: (id) => request(`/api/admin/tenants/${id}`),
  adjustCredits: (id, qty, reason) =>
    request(`/api/admin/tenants/${id}/credits`, { method: 'POST', body: { qty, reason } }),
  reingest: (tenantId, siteId) =>
    request(`/api/admin/tenants/${tenantId}/sites/${siteId}/reingest`, { method: 'POST' }),

  dashboard: (days) => request(`/api/admin/dashboard?days=${days}`),
  costs: (days) => request(`/api/admin/costs?days=${days}`),

  suspend: (id, on, reason) =>
    request(`/api/admin/tenants/${id}/suspend?on=${on}`, { method: 'POST', body: { reason } }),

  users: (query) => request(`/api/admin/users${query ? `?query=${encodeURIComponent(query)}` : ''}`),
  addUser: (body) => request('/api/admin/users', { method: 'POST', body }),
  revokeSessions: (id, reason) =>
    request(`/api/admin/users/${id}/revoke-sessions`, { method: 'POST', body: { reason } }),
  unlockUser: (id, reason) => request(`/api/admin/users/${id}/unlock`, { method: 'POST', body: { reason } }),
  verifyEmail: (id, reason) => request(`/api/admin/users/${id}/verify-email`, { method: 'POST', body: { reason } }),
  setPlatformAdmin: (id, grant, reason) =>
    request(`/api/admin/users/${id}/platform-admin?grant=${grant}`, { method: 'POST', body: { reason } }),
  setRole: (id, body) => request(`/api/admin/users/${id}/role`, { method: 'POST', body }),

  messages: (all) => request(`/api/admin/messages${all ? '?all=true' : ''}`),
  markAnswered: (id, answered) =>
    request(`/api/admin/messages/${id}/answered`, { method: 'POST', body: { answered } }),

  plans: () => request('/api/admin/plans'),
  editPlan: (id, body) => request(`/api/admin/plans/${id}`, { method: 'PUT', body }),
  addPlan: (body) => request('/api/admin/plans', { method: 'POST', body }),
  deletePlan: (id, reason) =>
    request(`/api/admin/plans/${id}?reason=${encodeURIComponent(reason)}`, { method: 'DELETE' }),
}
